import { cva } from 'class-variance-authority';

/**
 * Geometry from Figma set 2037:134558. Colour lives on `.ds-metric-card`.
 *
 * The root's `color` is the label colour, so the title, caption, icon and the InfoButton glyph
 * (which paints `currentColor`) all inherit `foreground/secondary`; only the value steps up.
 *
 * No transition anywhere: the card has no interactive state, and the set says the value never
 * animates, so loading swaps the skeleton for the number in one step. The skeleton's own pulse is
 * the only motion, and it stops when the number lands.
 *
 * Proportional figures, as Figma draws them. Google Sans Flex's tabular set pads every digit to
 * the 0's 19.5px advance at Heading/lg, so "1,240" renders 21% wider with visible gaps after the
 * 1 and the comma, and its tabular % is a narrower glyph (measured 2026-09-28).
 */
export const metricCardRecipe = cva(
  ['ds-metric-card box-border flex min-w-0 flex-col gap-2 font-sans text-[var(--ds-metric-card-label)]'],
  {
    variants: {
      layout: {
        // 200px has no sizing token; same gap as SelectableCard's 264px tile minimum.
        tile: 'min-w-[200px]',
        row: 'w-full',
      },
      surface: {
        filled: [
          'rounded-md border border-solid p-4',
          'border-[var(--ds-metric-card-border)] bg-[var(--ds-metric-card-bg)]',
        ],
        none: 'rounded-none border-0 bg-transparent px-0 py-2',
      },
    },
    defaultVariants: { layout: 'tile', surface: 'filled' },
  }
);

/**
 * Tile stacks title, value and caption with Figma's `x1` gap. Row is a grid so the value can sit
 * right of both the title and the caption while the DOM keeps the `dt`, value, caption reading
 * order; `x4` separates the two columns, and the text column itself has no gap.
 */
export const metricCardListRecipe = cva('m-0 min-w-0', {
  variants: {
    layout: {
      tile: 'flex flex-col gap-1',
      row: 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-4',
    },
  },
  defaultVariants: { layout: 'tile' },
});

/** Icon, then the title. The icon top-aligns to the first title line. */
export const metricCardTitleRecipe = cva('m-0 flex min-w-0 items-start gap-2', {
  variants: {
    layout: {
      tile: '',
      row: 'col-start-1 row-start-1 self-center',
    },
  },
  defaultVariants: { layout: 'tile' },
});

/** A 16px glyph in a 20px-tall box, so it centres on the first line of a wrapping title. */
export const metricCardIconRecipe = cva('flex h-5 w-4 shrink-0 items-center justify-center [&_svg]:size-4');

export const metricCardTitleLabelRecipe = cva('min-w-0 flex-1 text-label-lg [overflow-wrap:anywhere]');

/**
 * The Info button flows inline after the title's last word, x1 from it, so it stays next to the
 * words it explains in a Row and wraps with the title in a Tile. The 20px box matches the title's
 * line height, so the 16px button centres on whichever line it lands on.
 */
export const metricCardInfoRecipe = cva('ml-1 inline-flex h-5 items-center align-top');

export const metricCardValueRecipe = cva('m-0 text-[var(--ds-metric-card-value)] [overflow-wrap:anywhere]', {
  variants: {
    layout: {
      tile: 'text-heading-lg',
      row: 'col-start-2 row-start-1 self-center whitespace-nowrap text-right text-heading-sm',
    },
    hasCaption: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Spanning a second, empty row would stretch the title row to the value's 30px and pin the
    // title to its top, so Row spans only when a caption is there to fill that row.
    { layout: 'row', hasCaption: true, class: 'row-span-2' },
  ],
  defaultVariants: { layout: 'tile', hasCaption: false },
});

export const metricCardCaptionRecipe = cva('m-0 min-w-0 text-body-sm [overflow-wrap:anywhere]', {
  variants: {
    layout: {
      tile: '',
      row: 'col-start-1 row-start-2',
    },
    hasIcon: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Figma's Row puts the caption under the title, clear of the icon: 16px glyph + `x2` gap.
    { layout: 'row', hasIcon: true, class: 'ml-6' },
  ],
  defaultVariants: { layout: 'tile', hasIcon: false },
});

/**
 * The loading skeleton replaces the value inside a box the height of the value's line, so the card
 * does not change height when the number lands. Tile 96×24, Row 64×20 as drawn; 96px is off the
 * spacing scale, so it is an arbitrary value.
 */
export const metricCardPlaceholderRecipe = cva('flex items-center', {
  variants: {
    layout: {
      tile: 'h-[var(--ds-type-heading-lg-leading)]',
      row: 'h-[var(--ds-type-heading-sm-leading)] justify-end',
    },
  },
  defaultVariants: { layout: 'tile' },
});

/** Sizes the shared `Skeleton`; its fill and pulse are not restated here. */
export const metricCardSkeletonRecipe = cva('rounded-sm', {
  variants: {
    layout: {
      tile: 'h-6 w-[96px]',
      row: 'h-5 w-16',
    },
  },
  defaultVariants: { layout: 'tile' },
});

export const metricCardFooterRecipe = cva('flex min-w-0 flex-row flex-wrap items-center gap-2');
